import React from 'react';
import { FlowNode } from '../../../types/flowBuilder';

// Trigger configs
import WebhookTriggerConfig from './triggers/WebhookTriggerConfig';
import ScheduleTriggerConfig from './triggers/ScheduleTriggerConfig';
import EmailTriggerConfig from './triggers/EmailTriggerConfig';
import FileUploadTriggerConfig from './triggers/FileUploadTriggerConfig';
import TelegramTriggerConfig from './triggers/TelegramTriggerConfig';

// Action configs
import MultiConditionConfig from './actions/MultiConditionConfig';
import WaitConfig from './actions/WaitConfig';
import ConditionConfig from './actions/ConditionConfig';
import SwitchConfig from './actions/SwitchConfig';
import LoopConfig from './actions/LoopConfig';
import MergeConfig from './actions/MergeConfig';
import ApprovalConfig from './actions/ApprovalConfig';
import ActionConfig from './actions/ActionConfig';
import FormConfig from './actions/FormConfig';

// Data Processing configs
import VariableSetConfig from './dataProcessing/VariableSetConfig';
import TextProcessorConfig from './dataProcessing/TextProcessorConfig';
import DebugLoggerConfig from './dataProcessing/DebugLoggerConfig';
import DateTimeConfig from './dataProcessing/DateTimeConfig';
import RandomGeneratorConfig from './dataProcessing/RandomGeneratorConfig';
import JSONProcessorConfig from './dataProcessing/JSONProcessorConfig';
import CounterConfig from './dataProcessing/CounterConfig';
import DataFilterConfig from './dataProcessing/DataFilterConfig';
import DataSortConfig from './dataProcessing/DataSortConfig';
import StringFormatterConfig from './dataProcessing/StringFormatterConfig';
import ListProcessorConfig from './dataProcessing/ListProcessorConfig';
import DataTransformConfig from './dataProcessing/DataTransformConfig';

// Communication configs
import SMTPEmailConfig from './communication/SMTPEmailConfig';
import TelegramMessageConfig from './communication/TelegramMessageConfig';
import SMSSendConfig from './notifications/SMSSendConfig';
import NotificationConfig from './communication/NotificationConfig';

// Integration configs
import FlintSignConfig from './integrations/FlintSignConfig';
import IntegrationConfig from './integrations/IntegrationConfig';

// CRM configs
import CRMActionConfig from './crm/CRMActionConfig';

// AI configs
import OpenAIChatConfig from './ai/OpenAIChatConfig';
import ClaudeChatConfig from './ai/ClaudeChatConfig';
import AITextGeneratorConfig from './ai/AITextGeneratorConfig';
import AIImageGeneratorConfig from './ai/AIImageGeneratorConfig';
import AITextAnalyzerConfig from './ai/AITextAnalyzerConfig';
import AITranslatorConfig from './ai/AITranslatorConfig';
import AISummarizerConfig from './ai/AISummarizerConfig';

// File operation configs
import FileReadConfig from './fileOperations/FileReadConfig';
import FileWriteConfig from './fileOperations/FileWriteConfig';
import PDFGeneratorConfig from './fileOperations/PDFGeneratorConfig';
import ExcelProcessorConfig from './fileOperations/ExcelProcessorConfig';
import ImageProcessorConfig from './fileOperations/ImageProcessorConfig';
import FileConverterConfig from './fileOperations/FileConverterConfig';

// Data parser configs
import CSVParserConfig from './dataParsers/CSVParserConfig';
import XMLParserConfig from './dataParsers/XMLParserConfig';
import YAMLParserConfig from './dataParsers/YAMLParserConfig';
import Base64CoderConfig from './dataParsers/Base64CoderConfig';
import DataValidatorConfig from './dataParsers/DataValidatorConfig';

// Database configs
import DatabaseQueryConfig from './database/DatabaseQueryConfig';

// CRM configs (CRMActionConfig már importálva fent)
import CRMQueryConfig from './crm/CRMQueryConfig';
import GetContactPersonsConfig from './crm/GetContactPersonsConfig';
import MissingItemsQueryConfig from './crm/MissingItemsQueryConfig';

// Output/Display configs
import OutputDisplayConfig from './dataProcessing/OutputDisplayConfig';

// Error handling configs
import TryCatchConfig from './errorHandling/TryCatchConfig';
import RetryConfig from './errorHandling/RetryConfig';
import ErrorLoggerConfig from './errorHandling/ErrorLoggerConfig';
import TimeoutConfig from './errorHandling/TimeoutConfig';

// Shared helpers
import { BaseConfigProps } from './shared/ConfigFormHelpers';

interface NodeConfigRouterProps extends BaseConfigProps {
  node: FlowNode;
  updateConfig: (key: string, value: any) => void;
}

const NodeConfigRouter: React.FC<NodeConfigRouterProps> = ({ node, updateConfig }) => {
  // Node típus alapján a megfelelő konfigurációs komponens betöltése
  switch (node.type) {
    // Trigger típusok
    case 'trigger':
    case 'webhook_trigger':
      return <WebhookTriggerConfig node={node} updateConfig={updateConfig} />;
    
    case 'schedule_trigger':
      return <ScheduleTriggerConfig node={node} updateConfig={updateConfig} />;
    
    case 'form_trigger':
      return (
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Űrlap azonosító
            </label>
            <input
              type="text"
              value={node.data.config.formId || ''}
              onChange={(e) => updateConfig('formId', e.target.value)}
              className="w-full px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500 dark:bg-gray-700 dark:text-white"
              placeholder="form_12345"
            />
          </div>
          
          <div className="flex items-center">
            <input
              type="checkbox"
              id="validateRequired"
              checked={node.data.config.validateRequired || false}
              onChange={(e) => updateConfig('validateRequired', e.target.checked)}
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
            />
            <label htmlFor="validateRequired" className="ml-2 text-sm text-gray-700 dark:text-gray-300">
              Kötelező mezők ellenőrzése
            </label>
          </div>
        </div>
      );

    case 'email_trigger':
      return <EmailTriggerConfig node={node} updateConfig={updateConfig} />;

    case 'file_trigger':
      return <FileUploadTriggerConfig node={node} updateConfig={updateConfig} />;

    case 'database_trigger':
      return (
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Tábla név
            </label>
            <input
              type="text"
              value={node.data.config.table || ''}
              onChange={(e) => updateConfig('table', e.target.value)}
              className="w-full px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500 dark:bg-gray-700 dark:text-white"
              placeholder="customers"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Művelet típus
            </label>
            <select
              value={node.data.config.operation || 'insert'}
              onChange={(e) => updateConfig('operation', e.target.value)}
              className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="insert">Beszúrás</option>
              <option value="update">Frissítés</option>
              <option value="delete">Törlés</option>
            </select>
          </div>
        </div>
      );

    case 'manual_trigger':
      return (
        <div className="space-y-3">
          <div className="flex items-center">
            <input
              type="checkbox"
              id="requireConfirmation"
              checked={node.data.config.requireConfirmation || false}
              onChange={(e) => updateConfig('requireConfirmation', e.target.checked)}
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
            />
            <label htmlFor="requireConfirmation" className="ml-2 text-sm text-gray-700 dark:text-gray-300">
              Megerősítés szükséges
            </label>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Gomb szöveg
            </label>
            <input
              type="text"
              value={node.data.config.buttonText || 'Indítás'}
              onChange={(e) => updateConfig('buttonText', e.target.value)}
              className="w-full px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500 dark:bg-gray-700 dark:text-white"
              placeholder="Flow indítása"
            />
          </div>
        </div>
      );

    case 'telegram_trigger':
      return <TelegramTriggerConfig node={node} updateConfig={updateConfig} />;

    // Logic típusok
    case 'multi_condition':
      return <MultiConditionConfig node={node} updateConfig={updateConfig} />;

    // Action típusok
    case 'wait':
      return <WaitConfig node={node} updateConfig={updateConfig} />;

    case 'condition':
      return <ConditionConfig node={node} updateConfig={updateConfig} />;

    case 'switch':
      return <SwitchConfig node={node} updateConfig={updateConfig} />;

    case 'loop':
      return <LoopConfig node={node} updateConfig={updateConfig} />;

    case 'merge':
      return <MergeConfig node={node} updateConfig={updateConfig} />;

    case 'approval':
      return <ApprovalConfig node={node} updateConfig={updateConfig} />;

    case 'action':
      return <ActionConfig node={node} updateConfig={updateConfig} />;

    case 'form':
      return <FormConfig node={node} updateConfig={updateConfig} />;

    // Data Processing típusok
    case 'variable_set':
      return <VariableSetConfig node={node} updateConfig={updateConfig} />;

    case 'text_processor':
      return <TextProcessorConfig node={node} updateConfig={updateConfig} />;

    case 'variable_get':
      return (
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Változó neve
            </label>
            <input
              type="text"
              value={node.data.config.variableName || ''}
              onChange={(e) => updateConfig('variableName', e.target.value)}
              className="w-full px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500 dark:bg-gray-700 dark:text-white"
              placeholder="my_variable"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              A lekérdezendő változó neve
            </p>
          </div>
          <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md p-3">
            <div className="text-xs text-gray-600 dark:text-gray-400">
              <strong>Eredmény változók:</strong>
              <br />• <code>retrieved_value</code> - A változó értéke
              <br />• <code>variable_name</code> - A változó neve
            </div>
          </div>
        </div>
      );

    case 'math_operation':
      return (
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Művelet típusa
            </label>
            <select
              value={node.data.config.operation || 'add'}
              onChange={(e) => updateConfig('operation', e.target.value)}
              className="w-full px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500 dark:bg-gray-700 dark:text-white"
            >
              <option value="add">Összeadás (+)</option>
              <option value="subtract">Kivonás (-)</option>
              <option value="multiply">Szorzás (×)</option>
              <option value="divide">Osztás (÷)</option>
              <option value="power">Hatványozás (^)</option>
              <option value="modulo">Maradékos osztás (%)</option>
            </select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                A érték
              </label>
              <input
                type="text"
                value={node.data.config.a || ''}
                onChange={(e) => updateConfig('a', e.target.value)}
                className="w-full px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500 dark:bg-gray-700 dark:text-white"
                placeholder="10 vagy {{variable}}"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                B érték
              </label>
              <input
                type="text"
                value={node.data.config.b || ''}
                onChange={(e) => updateConfig('b', e.target.value)}
                className="w-full px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500 dark:bg-gray-700 dark:text-white"
                placeholder="5 vagy {{variable}}"
              />
            </div>
          </div>
        </div>
      );

    case 'debug_logger':
      return <DebugLoggerConfig node={node} updateConfig={updateConfig} />;

    case 'date_time':
      return <DateTimeConfig node={node} updateConfig={updateConfig} />;

    case 'random_generator':
      return <RandomGeneratorConfig node={node} updateConfig={updateConfig} />;

    case 'json_processor':
      return <JSONProcessorConfig node={node} updateConfig={updateConfig} />;

    case 'counter':
      return <CounterConfig node={node} updateConfig={updateConfig} />;

    case 'data_filter':
      return <DataFilterConfig node={node} updateConfig={updateConfig} />;

    case 'data_sort':
      return <DataSortConfig node={node} updateConfig={updateConfig} />;

    case 'string_formatter':
      return <StringFormatterConfig node={node} updateConfig={updateConfig} />;

    case 'list_processor':
      return <ListProcessorConfig node={node} updateConfig={updateConfig} />;

    case 'data_transform':
      return <DataTransformConfig node={node} updateConfig={updateConfig} />;

    // Communication típusok
    case 'smtp_email':
      return <SMTPEmailConfig node={node} updateConfig={updateConfig} />;

    case 'telegram_message':
      return <TelegramMessageConfig node={node} updateConfig={updateConfig} />;

    case 'sms_send':
      return <SMSSendConfig node={node} updateConfig={updateConfig} />;

    case 'seeme_sms_send':
      return <SMSSendConfig node={node} updateConfig={updateConfig} />;

    case 'notification':
      return <NotificationConfig node={node} updateConfig={updateConfig} />;

    // Integration típusok
    case 'integration':
      return <IntegrationConfig node={node} updateConfig={updateConfig} />;

    case 'flintsign_sign':
      return <FlintSignConfig node={node} updateConfig={updateConfig} />;

    // CRM típusok
    case 'crm_action':
      return <CRMActionConfig node={node} updateConfig={updateConfig} />;
    
    case 'crm_query':
      return <CRMQueryConfig node={node} updateConfig={updateConfig} />;
    
    case 'get_contact_persons':
      return <GetContactPersonsConfig node={node} updateConfig={updateConfig} />;
    
    case 'missing_items_query':
      return <MissingItemsQueryConfig node={node} updateConfig={updateConfig} />;
    
    case 'output_display':
      return <OutputDisplayConfig node={node} updateConfig={updateConfig} />;

    // AI Integration node-ok
    case 'openai_chat':
      return <OpenAIChatConfig node={node} updateConfig={updateConfig} />;

    case 'claude_chat':
      return <ClaudeChatConfig node={node} updateConfig={updateConfig} />;

    case 'deepseek_chat':
    case 'gemini_chat':
      return (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              API Kulcs
            </label>
            <input
              type="password"
              value={node.data.config?.apiKey || ''}
              onChange={(e) => updateConfig('apiKey', e.target.value)}
              placeholder="API kulcs..."
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Modell
            </label>
            <input
              type="text"
              value={node.data.config?.model || (node.type === 'deepseek_chat' ? 'deepseek-chat' : 'gemini-pro')}
              onChange={(e) => updateConfig('model', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Prompt
            </label>
            <textarea
              value={node.data.config?.prompt || ''}
              onChange={(e) => updateConfig('prompt', e.target.value)}
              placeholder="Add meg a kérdést vagy utasítást..."
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
            />
          </div>
        </div>
      );

    case 'ai_text_generator':
      return <AITextGeneratorConfig node={node} updateConfig={updateConfig} />;

    case 'ai_image_generator':
      return <AIImageGeneratorConfig node={node} updateConfig={updateConfig} />;

    case 'ai_text_analyzer':
      return <AITextAnalyzerConfig node={node} updateConfig={updateConfig} />;

    case 'ai_translator':
      return <AITranslatorConfig node={node} updateConfig={updateConfig} />;

    case 'ai_summarizer':
      return <AISummarizerConfig node={node} updateConfig={updateConfig} />;

    // File operation types
    case 'file_read':
      return <FileReadConfig node={node} updateConfig={updateConfig} />;
    
    case 'file_write':
      return <FileWriteConfig node={node} updateConfig={updateConfig} />;
    
    case 'pdf_generator':
      return <PDFGeneratorConfig node={node} updateConfig={updateConfig} />;
    
    case 'excel_processor':
      return <ExcelProcessorConfig node={node} updateConfig={updateConfig} />;
    
    case 'image_processor':
      return <ImageProcessorConfig node={node} updateConfig={updateConfig} />;
    
    case 'file_converter':
      return <FileConverterConfig node={node} updateConfig={updateConfig} />;

    // Data parser types
    case 'csv_parser':
      return <CSVParserConfig node={node} updateConfig={updateConfig} />;
    
    case 'xml_parser':
      return <XMLParserConfig node={node} updateConfig={updateConfig} />;
    
    case 'yaml_parser':
      return <YAMLParserConfig node={node} updateConfig={updateConfig} />;
    
    case 'base64_coder':
      return <Base64CoderConfig node={node} updateConfig={updateConfig} />;
    
    case 'data_validator':
      return <DataValidatorConfig node={node} updateConfig={updateConfig} />;

    // Database types
    case 'database_query':
    case 'database_insert':
    case 'database_update':
    case 'database_delete':
      return <DatabaseQueryConfig node={node} updateConfig={updateConfig} />;

    // Error handling types
    case 'try_catch':
      return <TryCatchConfig node={node} updateConfig={updateConfig} />;
    
    case 'retry':
      return <RetryConfig node={node} updateConfig={updateConfig} />;
    
    case 'error_logger':
      return <ErrorLoggerConfig node={node} updateConfig={updateConfig} />;
    
    case 'timeout':
      return <TimeoutConfig node={node} updateConfig={updateConfig} />;

    // Egyéb node típusok - egyelőre alapértelmezett üzenet
    default:
      return (
        <div className="text-sm text-gray-500 dark:text-gray-400">
          Ez a node típus ({node.type}) még nem támogatja a konfigurációt.
          <br />
          <span className="text-xs">Hamarosan elérhető lesz!</span>
        </div>
      );
  }
};

export default NodeConfigRouter;
